import "moment/locale/pt-br";
import "react-datetime/css/react-datetime.css";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Box,
    FormControl,
    FormLabel,
    Input,
    Select,
    SimpleGrid,
    useColorModeValue,
    useDisclosure
} from "@chakra-ui/react";
import DateTime from "react-datetime";
import { FilterContainer } from "components/Tables/FilterContainer";
import { LOCALE_PT_BR } from "constants/date-time";
import SearchSelect from "components/SearchSelect/SearchSelect";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { agentsService } from "services/agentsService";
import { trafficViolationTypeService } from "services/trafficViolationTypeService";
import { reportsService } from "services/reportsService";
import { ViolationStatus } from "services/trafficViolationService";
import { PaymentStatus, PaymentType, ObservationType } from "constants/reports";
import { useExportHandler } from "../useExportHandler";
import Loading from "components/Loading/Loading";

export const QualitativeViolationsFilter = ({ onFilter }) => {
    const { t } = useTranslation("common");
    const { isOpen, onToggle } = useDisclosure();
    const showToast = useCustomToast();
    const placeholderColor = useColorModeValue("gray.300", "gray.400");
    const [agents, setAgents] = useState([]);
    const [aitTypes, setAitTypes] = useState([]);
    const [filters, setFilters] = useState({
        agentId: "",
        violationType: "",
        street: "",
        startDate: "",
        endDate: "",
        status: ViolationStatus.VALIDATED.value,
        payment: PaymentStatus.ALL.value,
        paymentType: PaymentType.ALL.value,
        observationType: "",
        paymentStartDate: "",
        paymentEndDate: "",
    });

    const fetchAgents = async () => {
        try {
            const response = await agentsService.list({ pageSize: 999 });
            setAgents(response?.content || []);
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const fetchAitTypes = async () => {
        try {
            const response = await trafficViolationTypeService.list({ pageSize: 999 });
            setAitTypes(response?.content || []);
        } catch (err) {
            showToast({ title: t(err), status: "error" });
        }
    };

    const handleChange = (field) => (e) => {
        const value = e?.target ? e.target.value : e;
        setFilters((prev) => ({ ...prev, [field]: value }));
    };

    const onFilterHandler = () => {
        onFilter(getFilters());
    };

    const onClearHandler = () => {
        const defaultFilters = {
            agentId: "",
            violationType: "",
            street: "",
            startDate: "",
            endDate: "",
            status: ViolationStatus.VALIDATED.value,
            payment: PaymentStatus.ALL.value,
            paymentType: PaymentType.ALL.value,
            observationType: "",
            paymentStartDate: "",
            paymentEndDate: "",
        };

        setFilters(defaultFilters);
        onFilter(filters);
    };

    const getFilters = () => {
        const filtersPayload = {
            id_agente: filters.agentId,
            id_tipo_infracao: filters.violationType,
            logradouro_local_infracao: filters.street,
            data_inicial: filters.startDate ? new Date(filters.startDate).toLocaleDateString(LOCALE_PT_BR) : undefined,
            data_final: filters.endDate ? new Date(filters.endDate).toLocaleDateString(LOCALE_PT_BR) : undefined,
            status: filters.status,
            pagamento: filters.payment,
            tipo_pagamento: filters.paymentType,
            tipo_observacao: filters.observationType,
            data_pagamento_inicial: filters.paymentStartDate ? new Date(filters.paymentStartDate).toLocaleDateString(LOCALE_PT_BR) : undefined,
            data_pagamento_final: filters.paymentEndDate ? new Date(filters.paymentEndDate).toLocaleDateString(LOCALE_PT_BR) : undefined,
        };

        return Object.fromEntries(
            Object.entries(filtersPayload).filter(([, value]) => value !== "" && value !== undefined && value !== null)
        );
    };

    const [handleExportQualitativeViolation, isLoadingExportQualitativeViolation] = useExportHandler(
        reportsService.export,
        "qualitativo-infracoes.xlsx"
    );

    const handleToggle = () => {
        onToggle();
    };

    useEffect(() => {
        fetchAgents();
        fetchAitTypes();
    }, []);

    return (
        <>
            {isLoadingExportQualitativeViolation ? (
                <Box
                    position="fixed"
                    top="0"
                    left="0"
                    right="0"
                    bottom="0"
                    bg="rgba(255, 255, 255, 0.6)"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    zIndex="overlay"
                >
                    <Loading maxH={{ sm: "100px", md: "350px" }} />
                </Box>
            ) : (
                <FilterContainer
                    w="125px"
                    fontSize="sm"
                    fontWeight="bold"
                    variant="light"
                    isOpen={isOpen}
                    onToggle={handleToggle}
                    onFilter={onFilterHandler}
                    onClear={onClearHandler}
                    isExportable={true}
                    onExport={handleExportQualitativeViolation}
                >
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                        <FormControl>
                            <FormLabel>
                                {t("reports.violations-qualitative.filters.agent.label")}
                            </FormLabel>
                            <SearchSelect
                                items={agents}
                                valueField="id"
                                displayField={["codigo", "nome"]}
                                value={filters.agentId}
                                onChange={handleChange("agentId")}
                                placeholder={t("reports.violations-qualitative.filters.agent.placeholder")}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>
                                {t("reports.violations-qualitative.filters.violation-type.label")}
                            </FormLabel>
                            <SearchSelect
                                items={aitTypes}
                                valueField="id"
                                displayField={["codigo", "descricao"]}
                                value={filters.violationType}
                                onChange={handleChange("violationType")}
                                placeholder={t("reports.violations-qualitative.filters.violation-type.placeholder")}
                                isDisabled={!aitTypes.length}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>
                                {t("reports.violations-qualitative.filters.street.label")}
                            </FormLabel>
                            <Input
                                fontSize="sm"
                                value={filters.street}
                                onChange={handleChange("street")}
                                placeholder={t("reports.violations-qualitative.filters.street.placeholder")}
                                _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>
                                {t("reports.violations-qualitative.filters.start-date.label")}
                            </FormLabel>
                            <DateTime
                                locale={LOCALE_PT_BR}
                                timeFormat={false}
                                closeOnSelect
                                inputProps={filters.startDate ? undefined : { value: "", placeholder: t("system.placeholders.date") }}
                                renderInput={(props) => (
                                    <Input
                                        {...props}
                                        fontSize="sm"
                                        _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                                    />
                                )}
                                onChange={(date) => setFilters((prev) => ({ ...prev, startDate: date }))}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>
                                {t("reports.violations-qualitative.filters.end-date.label")}
                            </FormLabel>
                            <DateTime
                                locale={LOCALE_PT_BR}
                                timeFormat={false}
                                closeOnSelect
                                inputProps={filters.endDate ? undefined : { value: "", placeholder: t("system.placeholders.date") }}
                                renderInput={(props) => (
                                    <Input
                                        {...props}
                                        fontSize="sm"
                                        _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                                    />
                                )}
                                onChange={(date) => setFilters((prev) => ({ ...prev, endDate: date }))}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>
                                {t("reports.violations-qualitative.filters.status.label")}
                            </FormLabel>
                            <Select
                                fontSize="sm"
                                id="violation-status"
                                value={filters.status || ""}
                                onChange={handleChange("status")}
                                placeholder={t("reports.violations-qualitative.filters.status.placeholder")}
                            >
                                {Object.values(ViolationStatus).map((option, index) => (
                                    <option key={index} value={option.value}>
                                        {t(`traffic-violation.labels.status.${option.labelKey}`)}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>
                                {t("reports.violations-qualitative.filters.observation-type.label")}
                            </FormLabel>
                            <Select
                                fontSize="sm"
                                id="observation-type"
                                value={filters.observationType}
                                onChange={handleChange("observationType")}
                            >
                                {Object.values(ObservationType).map((option, index) => (
                                    <option key={index} value={option.value}>
                                        {t(`reports.violations-qualitative.filters.observation-type.${option.labelKey}`)}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>
                                {t("reports.violations-qualitative.filters.payment.label")}
                            </FormLabel>
                            <Select
                                fontSize="sm"
                                id="payment"
                                value={filters.payment}
                                onChange={handleChange("payment")}
                            >
                                {Object.values(PaymentStatus).map((option, index) => (
                                    <option key={index} value={option.value}>
                                        {t(`reports.violations-qualitative.filters.payment.${option.labelKey}`)}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>
                                {t("reports.violations-qualitative.filters.payment-type.label")}
                            </FormLabel>
                            <Select
                                fontSize="sm"
                                id="payment-type"
                                value={filters.paymentType}
                                onChange={handleChange("paymentType")}
                                placeholder={t("reports.violations-qualitative.filters.payment-type.placeholder")}
                            >
                                {Object.values(PaymentType).map((option, index) => (
                                    <option key={index} value={option.value}>
                                        {t(`reports.violations-qualitative.filters.payment-type.${option.labelKey}`)}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl>
                            <FormLabel>
                                {t("reports.violations-qualitative.filters.payment-start-date.label")}
                            </FormLabel>
                            <DateTime
                                locale={LOCALE_PT_BR}
                                timeFormat={false}
                                closeOnSelect
                                inputProps={filters.paymentStartDate ? undefined : { value: "", placeholder: t("system.placeholders.date") }}
                                renderInput={(props) => (
                                    <Input
                                        {...props}
                                        fontSize="sm"
                                        _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                                    />
                                )}
                                onChange={(date) => setFilters((prev) => ({ ...prev, paymentStartDate: date }))}
                            />
                        </FormControl>

                        <FormControl>
                            <FormLabel>
                                {t("reports.violations-qualitative.filters.payment-end-date.label")}
                            </FormLabel>
                            <DateTime
                                locale={LOCALE_PT_BR}
                                timeFormat={false}
                                closeOnSelect
                                inputProps={filters.paymentEndDate ? undefined : { value: "", placeholder: t("system.placeholders.date") }}
                                renderInput={(props) => (
                                    <Input
                                        {...props}
                                        fontSize="sm"
                                        _placeholder={{ color: placeholderColor, fontSize: "sm" }}
                                    />
                                )}
                                onChange={(date) => setFilters((prev) => ({ ...prev, paymentEndDate: date }))}
                            />
                        </FormControl>
                    </SimpleGrid>
                </FilterContainer>
            )}
        </>
    );
};